import { Avatar, Image, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { type RenderUserProfileProps } from "@sendbird/uikit-react/types/types";
import { useEffect, useState } from "react";
import { z } from "zod";

import { get } from "../api";
import { environmentConfig } from "../environment";
import { useCbhFlag } from "../FeatureFlags";
import { CbhFeatureFlag } from "../FeatureFlags/CbhFeatureFlag";
import { badgesList } from "./badges-list";

interface Badge {
  name: string;
  fileName: string;
}

export const getFSSProfilePicDistributionURL = async (workerId: string): Promise<string> => {
  const distributionURL = await get({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/agentProfile/${workerId}/profile-image-distribution-url`,
    responseSchema: z.object({
      distribution_url: z.string(),
    }),
  });

  return distributionURL.data?.distribution_url;
};

interface UserProfileProps {
  profileProps?: RenderUserProfileProps;
}

export function UserProfile(props: UserProfileProps): JSX.Element {
  const { profileProps } = props;
  const [profilePicture, setProfilePicture] = useState<string | undefined>(undefined);

  const basePath = process.env.REACT_APP_BASE_PATH ?? "/";

  const hackathonBadgesConfig = useCbhFlag(CbhFeatureFlag.HACKATHON_BADGES_CONFIG, {
    defaultValue: {
      users: [],
    },
  });

  const getBadges = (userId: string): Badge[] => {
    if (process.env.REACT_APP_ENVIRONMENT_NAME === "production") {
      const userBadges = badgesList.users.find((user) => user.userId === userId)?.badges ?? [];

      if (userBadges.length > 0) {
        return userBadges.map((badge) => ({
          name: badge,
          fileName: `${badge}.png`,
        }));
      }

      const hackathonBadges =
        hackathonBadgesConfig.users.find((user) => user.userId === userId)?.badges ?? [];

      return hackathonBadges.map((badge) => ({
        name: badge,
        fileName: `${badge}.png`,
      }));
    }

    return [];
  };

  // fetch badges from feature flag
  const badges = getBadges(profileProps?.user.userId ?? "");

  useEffect(() => {
    const fetchProfilePicture = async () => {
      const profilePicture = await getFSSProfilePicDistributionURL(profileProps?.user.userId ?? "");
      setProfilePicture(profilePicture);
    };

    void fetchProfilePicture();
  }, [profileProps?.user.userId]);

  return (
    <Stack direction="column" spacing={4} alignItems="center" p={3} width="100vw">
      <Stack spacing={2} alignItems="center">
        <Avatar src={profilePicture} size="large" />
        <Text semibold variant="body1">
          {profileProps?.user.nickname}
        </Text>
      </Stack>
      {badges.length > 0 && (
        <Stack spacing={4} alignItems="center" style={{ width: "100%" }}>
          <Text semibold variant="body1">
            Badges
          </Text>
          <Stack direction="row" spacing={4} alignItems="center">
            {badges.map((badge) => (
              <Stack key={badge.name} spacing={1} alignItems="center">
                <Image
                  src={`${basePath}assets/badges/${badge.fileName}`}
                  alt={badge.name}
                  sx={{
                    width: "50px",
                  }}
                />
                <Text variant="body2" align="center">
                  {badge.name
                    .replace(" - Gold", "")
                    .replace(" - Silver", "")
                    .replace(" - Bronze", "")}
                </Text>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
